/**
 * month-placement: which cells of a month grid an event appears in (CC 0477). An event is placed on EVERY calendar
 * date it covers, from its start date through its end date, in the workspace's zone, including one that began in an
 * earlier month. An end at exactly midnight does not spill into the next day. Each event counts once per month.
 */
import { calendarDateKey } from "../../lib/datetime";

export interface MonthEventIn { title: string; startsAt: string; endsAt?: string | null; color: string }
export interface MonthChip { title: string; color: string }

const addDays = (key: string, n: number): string => {
  const d = new Date(Date.UTC(+key.slice(0, 4), +key.slice(5, 7) - 1, +key.slice(8, 10) + n));
  return d.toISOString().slice(0, 10);
};
/** The last calendar date an event covers: the date of the instant just before its end, so 00:00 belongs to the day before. */
export function lastCoveredKey(startsAt: string, endsAt: string | null | undefined, tz?: string): string {
  const s = Date.parse(startsAt), e = endsAt ? Date.parse(endsAt) : NaN;
  if (!Number.isFinite(e) || e <= s) return calendarDateKey(startsAt, tz);
  return calendarDateKey(new Date(e - 1), tz);
}
/** day-of-month (1..31) → chips, for month `mo` (0-based) of year `y`, and how many events touch that month. */
export function placeMonthEvents(events: MonthEventIn[], y: number, mo: number, tz?: string): { byDay: Record<number, MonthChip[]>; count: number } {
  const first = `${y}-${String(mo + 1).padStart(2, "0")}-01`;
  const last = new Date(Date.UTC(y, mo + 1, 0)).toISOString().slice(0, 10);
  const byDay: Record<number, MonthChip[]> = {};
  let count = 0;
  for (const e of events) {
    if (!Number.isFinite(Date.parse(e.startsAt))) continue;
    const startKey = calendarDateKey(e.startsAt, tz);
    const endKey = lastCoveredKey(e.startsAt, e.endsAt, tz);
    if (endKey < first || startKey > last) continue;
    count += 1;
    for (let k = startKey < first ? first : startKey; k <= endKey && k <= last; k = addDays(k, 1)) {
      const d = +k.slice(8, 10);
      (byDay[d] = byDay[d] || []).push({ title: e.title, color: e.color });
    }
  }
  return { byDay, count };
}
